import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, MessageSquarePlus } from "lucide-react";
import { SERVICES } from "../../lib/content";

const STATUSES = ["planning", "in_progress", "review", "completed", "on_hold"];

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [updateMsg, setUpdateMsg] = useState("");
  const [form, setForm] = useState({
    name: "", client_id: "", description: "", service_type: SERVICES[0].title,
    status: STATUSES[0], start_date: "", target_completion: "", budget_cents: 0,
    assigned_admin_ids: [],
  });

  const load = () => api.get("/admin/projects").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/auth/admins").then((r) => setAdmins(r.data)).catch(() => setAdmins([]));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Select a client"); return; }
    try {
      await api.post("/admin/projects", { ...form, budget_cents: Number(form.budget_cents) || 0 });
      toast.success("Project created");
      setShowNew(false);
      setForm({ ...form, name: "", description: "", client_id: "", assigned_admin_ids: [] });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete project?")) return;
    await api.delete(`/admin/projects/${id}`);
    load();
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/projects/${id}`, { status });
    load();
  };

  const addUpdate = async (id) => {
    if (!updateMsg.trim()) return;
    try {
      await api.post(`/admin/projects/${id}/updates`, { message: updateMsg });
      setUpdateMsg("");
      load();
    } catch {
      toast.error("Failed to add update");
    }
  };

  const toggleAdmin = (aid) => {
    setForm((f) => ({
      ...f,
      assigned_admin_ids: f.assigned_admin_ids.includes(aid)
        ? f.assigned_admin_ids.filter((x) => x !== aid)
        : [...f.assigned_admin_ids, aid],
    }));
  };

  return (
    <div data-testid="admin-projects-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow text-[var(--crest-gold)]">— Projects</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">Project Management</h1>
        </div>
        <button className="btn-primary" onClick={() => setShowNew((v) => !v)} data-testid="projects-new-toggle"><Plus size={14} /> New Project</button>
      </div>

      {showNew && (
        <form onSubmit={create} className="admin-card grid md:grid-cols-2 gap-x-8 gap-y-2 mb-8" data-testid="project-create-form">
          <input className="crest-input md:col-span-2" placeholder="Project name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-form-name" />
          <select className="crest-input" required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="project-form-client">
            <option value="">Select client...</option>
            {clients.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <select className="crest-input" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} data-testid="project-form-service">
            {SERVICES.map((s) => <option key={s.id}>{s.title}</option>)}
          </select>
          <input className="crest-input" type="date" placeholder="Start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="project-form-start" />
          <input className="crest-input" type="date" placeholder="Target completion" value={form.target_completion} onChange={(e) => setForm({ ...form, target_completion: e.target.value })} data-testid="project-form-target" />
          <input className="crest-input md:col-span-2" type="number" placeholder="Budget in cents" value={form.budget_cents} onChange={(e) => setForm({ ...form, budget_cents: e.target.value })} data-testid="project-form-budget" />
          <textarea className="crest-input md:col-span-2" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-form-desc" />
          <div className="md:col-span-2 mt-2">
            <div className="eyebrow text-[var(--crest-muted)] mb-2">Assigned Team</div>
            <div className="flex flex-wrap gap-2">
              {admins.map((a) => {
                const sel = form.assigned_admin_ids.includes(a.id);
                return (
                  <button type="button" key={a.id} onClick={() => toggleAdmin(a.id)} className={`pill ${sel ? "bg-[var(--crest-black)] text-[var(--crest-cream)] border-[var(--crest-black)]" : ""}`} data-testid={`project-form-assign-${a.id}`}>
                    {sel && <span className="pill-dot" />} {a.name}
                  </button>
                );
              })}
            </div>
          </div>
          <button className="btn-primary md:col-span-2 mt-4" data-testid="project-form-submit">Create Project</button>
        </form>
      )}

      <div className="space-y-4">
        {items.length === 0 && <div className="admin-card text-sm text-[var(--crest-muted)]">No projects yet.</div>}
        {items.map((p) => (
          <div key={p._id} className="admin-card" data-testid={`project-row-${p._id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow text-[var(--crest-gold)]">{p.service_type}</div>
                <h3 className="font-serif text-2xl mt-1">{p.name}</h3>
                <div className="text-sm text-[var(--crest-muted)] mt-1">Client: {p.client_name}</div>
                {p.description && <p className="text-sm mt-3 text-[var(--crest-muted)] max-w-2xl">{p.description}</p>}
                <div className="flex flex-wrap gap-2 mt-3 text-xs text-[var(--crest-muted)]">
                  {p.start_date && <span>Start: {p.start_date}</span>}
                  {p.target_completion && <span>· Target: {p.target_completion}</span>}
                  {p.budget_cents ? <span>· Budget: ${(p.budget_cents / 100).toFixed(2)}</span> : null}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select className="crest-input text-xs" value={p.status} onChange={(e) => updateStatus(p._id, e.target.value)} data-testid={`project-status-${p._id}`}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(p._id)} className="text-[var(--crest-muted)] hover:text-red-600 p-2" data-testid={`project-delete-${p._id}`}><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="mt-5 border-t border-black/10 pt-4">
              <button onClick={() => setExpandedId(expandedId === p._id ? null : p._id)} className="text-xs gold tracking-widest uppercase flex items-center gap-2" data-testid={`project-updates-toggle-${p._id}`}>
                <MessageSquarePlus size={14} /> Updates ({(p.updates || []).length})
              </button>
              {expandedId === p._id && (
                <div className="mt-4 space-y-3">
                  {(p.updates || []).map((u) => (
                    <div key={u.id} className="text-sm border-l-2 border-[var(--crest-gold)] pl-4">
                      <div className="text-[var(--crest-muted)] text-xs">{u.author_name} · {(u.created_at || "").slice(0, 16).replace("T", " ")}</div>
                      <div className="mt-1">{u.message}</div>
                    </div>
                  ))}
                  <div className="flex gap-2 items-end">
                    <input className="crest-input flex-1" placeholder="Add a project update..." value={expandedId === p._id ? updateMsg : ""} onChange={(e) => setUpdateMsg(e.target.value)} data-testid={`project-update-input-${p._id}`} />
                    <button onClick={() => addUpdate(p._id)} className="btn-primary" data-testid={`project-update-submit-${p._id}`}>Post</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
